import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    const url = new URL(window.location.href);
    const shopify = window.Shopify;
    if (shopify?.previewMode && shopify.theme?.role === 'unpublished' && /^\d+$/.test(String(shopify.theme.id))) {
      // Keep the current preview session for normalization. Adding a preview
      // parameter here triggers Shopify's redirect, which collapses repeated facets.
      const previewInput = this.querySelector('[data-search-preview]');
      if (previewInput) {
        previewInput.value = String(shopify.theme.id);
        previewInput.disabled = false;
      }
      const resetLink = this.querySelector('.search__reset-button');
      if (resetLink) {
        const resetURL = new URL(resetLink.href, url.origin);
        resetURL.searchParams.set('preview_theme_id', String(shopify.theme.id));
        resetLink.href = resetURL.toString();
      }
    }
    // Products keep their clean URL; article searches retain their native type.
    const typeInput = this.querySelector('input[name="type"]');
    const articleSearch = typeInput?.value === 'article';
    if (typeInput) typeInput.disabled = !articleSearch;
    const resetLink = this.querySelector('.search__reset-button');
    if (resetLink) {
      const resetURL = new URL(resetLink.href, url.origin);
      if (!articleSearch) resetURL.searchParams.delete('type');
      resetLink.href = resetURL.toString();
    }
    document.querySelectorAll('[data-search-type-link], [data-search-pagination] a').forEach((link) => {
      if (shopify?.previewMode && shopify.theme?.role === 'unpublished') {
        const linkURL = new URL(link.href);
        linkURL.searchParams.set('preview_theme_id', String(shopify.theme.id));
        link.href = linkURL.toString();
      }
    });
    if (!articleSearch && url.searchParams.has('type')) {
      url.searchParams.delete('type');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
